<template>
  <div class="mpm-input-wrapper">
    <a-input
      ref="input"
      :value="innerValue"
      :placeholder="placeholder"
      :disabled="disabled"
      :maxLength="maxLength"
      :allowClear="allowClear"
      :size="size"
      :type="type"
      @input="onInput"
      @change="onChange"
      @pressEnter="onPressEnter"
      @focus="onFocus"
      @blur="onBlur"
      v-bind="$attrs"
    >
      <template v-if="$slots.prefix" slot="prefix">
        <slot name="prefix" />
      </template>
      <template v-if="$slots.suffix" slot="suffix">
        <slot name="suffix" />
      </template>
    </a-input>
  </div>
</template>

<script>
  export default {
    name: 'MpmInput',
    inheritAttrs: false,
    props: {
      // 输入框的值
      value: {
        type: [String, Number],
        default: '',
      },
      // 占位符
      placeholder: {
        type: String,
        default: '请输入',
      },
      // 是否禁用
      disabled: {
        type: Boolean,
        default: false,
      },
      // 最大长度
      maxLength: {
        type: [String, Number],
        default: undefined,
      },
      // 是否可清除
      allowClear: {
        type: Boolean,
        default: false,
      },
      // 输入框大小
      size: {
        type: String,
        default: 'default',
        validator: function (value) {
          return ['large', 'default', 'small'].indexOf(value) !== -1
        },
      },
      // 输入框类型
      type: {
        type: String,
        default: 'text',
      },
      // 是否自动去除前后空格
      trim: {
        type: Boolean,
        default: true,
      },
      // 是否过滤所有空格
      filterSpace: {
        type: Boolean,
        default: false,
      },
      // 最小值（用于 number 类型）
      min: {
        type: [String, Number],
        default: undefined,
      },
      // 最大值（用于 number 类型）
      max: {
        type: [String, Number],
        default: undefined,
      },
      // 小数位数（用于 number 类型）
      decimalPlaces: {
        type: [String, Number],
        default: 2,
      },
    },
    data() {
      return {
        innerValue: this.formatValue(this.value),
        isComposing: false, // 用于处理中文输入
      }
    },
    watch: {
      value: {
        handler(newVal) {
          this.innerValue = newVal
        },
        immediate: true,
      },
    },
    methods: {
      // 格式化值
      formatValue(val) {
        if (val === null || val === undefined) {
          return ''
        }
        let formattedValue = String(val)
        // 过滤所有空格
        if (this.filterSpace) {
          formattedValue = formattedValue.replace(/\s/g, '')
        }
        // 去除前后空格
        else if (this.trim) {
          formattedValue = formattedValue.trim()
        }
        return formattedValue
      },
      // 格式化数字值
      formatNumberValue(val) {
        if (!val) return ''

        let value = String(val)
        // 去除所有非数字和小数点的字符
        value = value.replace(/[^\d.]/g, '')
        // 只保留第一个小数点
        value = value.replace(/\.{2,}/g, '.')
        value = value.replace('.', '$#$').replace(/\./g, '').replace('$#$', '.')

        // 分离整数部分和小数部分
        const parts = value.split('.')
        let integerPart = parts[0] || ''
        let decimalPart = parts[1] || ''

        // 限制小数位数
        if (this.decimalPlaces !== undefined && this.decimalPlaces !== null) {
          const maxDecimal = Number(this.decimalPlaces)
          if (decimalPart.length > maxDecimal) {
            decimalPart = decimalPart.substring(0, maxDecimal)
          }
        }

        // 去除整数部分多余的前导0（但保留小数点前的0，如 0.5）
        if (integerPart !== '0' && integerPart !== '0.') {
          integerPart = integerPart.replace(/^0+(\d)/, '$1')
        }

        // 重新组合
        if (decimalPart) {
          value = integerPart + '.' + decimalPart
        } else if (value.includes('.')) {
          value = integerPart + '.'
        } else {
          value = integerPart
        }

        // 转换为数字进行范围检查
        const numValue = parseFloat(value)
        if (!isNaN(numValue)) {
          // 检查最小值
          if (this.min !== undefined && numValue < Number(this.min)) {
            value = String(this.min)
          }
          // 检查最大值
          if (this.max !== undefined && numValue > Number(this.max)) {
            value = String(this.max)
          }
        }

        return value
      },
      // 处理输入事件
      onInput(e) {
        // 获取输入值并格式化
        let value = e.target.value

        // 如果是 number 类型，进行数字格式化
        if (this.type === 'number') {
          const originalValue = value
          value = this.formatNumberValue(value)

          // 如果格式化后的值与输入值不同，立即更新输入框的值
          if (value !== originalValue) {
            // 保存当前光标位置
            const input = e.target
            const cursorPos = input.selectionStart

            // 直接设置输入框的值
            input.value = value

            // 更新内部值
            this.innerValue = value

            // 计算新的光标位置
            const lengthDiff = value.length - originalValue.length
            const newCursorPos = Math.max(0, Math.min(cursorPos + lengthDiff, value.length))

            // 使用 $nextTick 确保 DOM 更新后再设置光标位置
            this.$nextTick(() => {
              input.setSelectionRange(newCursorPos, newCursorPos)
            })
          } else {
            this.innerValue = value
          }
        } else {
          this.innerValue = value
        }

        this.$emit('input', value)
      },
      // 处理change事件
      onChange(e) {
        let value = e.target.value
        // 如果是 number 类型，进行数字格式化
        if (this.type === 'number') {
          value = this.formatNumberValue(value)
        } else {
          value = this.formatValue(value)
        }
        this.innerValue = value
        this.$emit('change', value)
      },
      // 处理回车事件
      onPressEnter(e) {
        const value = this.formatValue(e.target.value)
        this.$emit('pressEnter', value)
      },
      // 处理获得焦点事件
      onFocus(e) {
        this.$emit('focus', e)
      },
      // 处理失去焦点事件
      onBlur(e) {
        // 在失去焦点时确保进行最后的格式化
        let value = e.target.value
        // 如果是 number 类型，进行数字格式化
        if (this.type === 'number') {
          value = this.formatNumberValue(value)
        } else {
          value = this.formatValue(value)
        }
        if (this.innerValue !== value) {
          this.innerValue = value
          this.$emit('input', value)
        }
        this.$emit('blur', e)
      },
      // 对外暴露的方法：获取焦点
      focus() {
        this.$refs.input.focus()
      },
      // 对外暴露的方法：失去焦点
      blur() {
        this.$refs.input.blur()
      },
      // 对外暴露的方法：选择内容
      select() {
        this.$refs.input.select()
      },
    },
  }
</script>

<style lang="scss" scoped>
  .mpm-input-wrapper {
    display: inline-block;
    width: 100%;
  }
</style>
